/**
 * CaseReviewModal (Fix 21) — the full-case "View all" review window with a
 * blurred backdrop. Shows every detail an admin needs to decide whether to
 * approve or send a case back — scholarship split, open/close window, team
 * size, eligibility, brief, deliverables, tier — and lets the admin EDIT the
 * competition-level fields inline (name, brief, deliverable description,
 * dates, team size, tier) and save. Deliverable rows, rubric weights, and
 * eligibility detail live in the full client editor, which admin RLS already
 * lets us write — a button deep-links there.
 *
 * Registered as an overlay demo on /brand (DemoOverlays → "Admin case review").
 * Theme-driven via the shared admin/ui kit.
 */
import type { Href } from "expo-router";
import { router } from "expo-router";
import { useMemo, useState } from "react";
import { Text, View } from "react-native";

import { formatCents, type CaseForReview } from "../../../lib/caseApprovals";
import type { CompetitionTier } from "../../../types/database";
import { updateCompetition } from "../../../lib/clientCompetitions";
import { logAdminAction } from "../../../lib/auditLog";
import { toIsoOrThrow } from "../../../lib/datetime";
import { RADII, SPACING, STROKES } from "../../../lib/brand";
import { PANE_THEMES } from "../../brand/paneThemes";
import {
  AdminBanner,
  AdminButton,
  AdminInput,
  AdminModal,
  FilterChip,
} from "../ui";
import { SectionHeader } from "../../aspects/SectionHeader";
import { DetailRow } from "./parts";
import { engagementLabel, formatFriendlyDate } from "./helpers";

const THEME = PANE_THEMES.Admin;

type PrizeRow = { place?: number; amount_cents?: number; label?: string | null };

type EditForm = {
  name: string;
  brief: string;
  deliverable_description: string;
  tier: CompetitionTier | null;
  opens_at: string;
  closes_at: string;
  team_size_min: string;
  max_team_size: string;
};

function toForm(d: CaseForReview): EditForm {
  return {
    name: d.name ?? "",
    brief: d.brief ?? "",
    deliverable_description: d.deliverable_description ?? "",
    tier: (d.tier as CompetitionTier | null) ?? null,
    opens_at: d.opens_at ? String(d.opens_at).slice(0, 16) : "",
    closes_at: d.closes_at ? String(d.closes_at).slice(0, 16) : "",
    team_size_min: String(d.team_size_min ?? 1),
    max_team_size: String(d.max_team_size ?? 4),
  };
}

const ALL_TIERS: CompetitionTier[] = ["100", "250", "500", "unlimited"];

export function CaseReviewModal({
  detail,
  onClose,
  onSaved,
}: {
  detail: CaseForReview;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState<EditForm>(() => toForm(detail));
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [savedNote, setSavedNote] = useState<string | null>(null);

  const prizeRows = useMemo<PrizeRow[]>(
    () => (Array.isArray(detail.prize_structure) ? (detail.prize_structure as PrizeRow[]) : []),
    [detail.prize_structure],
  );
  const scholarshipTotalCents = useMemo(
    () => prizeRows.reduce((s, p) => s + (typeof p.amount_cents === "number" ? p.amount_cents : 0), 0),
    [prizeRows],
  );

  async function onSave() {
    setErr(null);
    setSavedNote(null);
    // Validate the window (reuse the client editor's invariant).
    let opensIso: string | null;
    let closesIso: string | null;
    try {
      opensIso = toIsoOrThrow(form.opens_at, "Opens at");
      closesIso = toIsoOrThrow(form.closes_at, "Closes at");
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Invalid date");
      return;
    }
    if (!opensIso || !closesIso) {
      setErr("Opens-at and closes-at are both required.");
      return;
    }
    if (new Date(closesIso).getTime() <= new Date(opensIso).getTime()) {
      setErr("Closes-at must be after opens-at.");
      return;
    }
    const minTeam = Math.max(1, parseInt(form.team_size_min, 10) || 1);
    const maxTeam = Math.max(1, parseInt(form.max_team_size, 10) || 4);
    if (maxTeam < minTeam) {
      setErr("Max team size must be ≥ min team size.");
      return;
    }

    setSaving(true);
    const patch = {
      name: form.name.trim() || detail.name,
      brief: form.brief.trim() || null,
      deliverable_description: form.deliverable_description.trim() || null,
      tier: form.tier,
      opens_at: opensIso,
      closes_at: closesIso,
      team_size_min: minTeam,
      max_team_size: maxTeam,
    };
    const { error } = await updateCompetition(detail.id, patch);
    setSaving(false);
    if (error) {
      setErr(error.message);
      return;
    }
    void logAdminAction({
      action: "competition.admin_review_edit",
      entityType: "competitions",
      entityId: detail.id,
      summary: `Edited case from the review modal: ${patch.name}`,
      diff: { after: { tier: patch.tier, opens_at: patch.opens_at, closes_at: patch.closes_at } },
    });
    setEditing(false);
    setSavedNote("Saved. The client side reflects these changes.");
    onSaved();
  }

  return (
    <AdminModal
      theme={THEME}
      visible
      onClose={onClose}
      blurBackdrop
      maxWidth={760}
      eyebrow="Full case review"
      title={detail.name}
      subtitle={`${detail.company_name ?? "—"}${detail.client_full_name ? ` · ${detail.client_full_name}` : ""} · ${engagementLabel(detail.engagement_model as never)}`}
      footer={
        editing ? (
          <>
            <AdminButton theme={THEME} label="Cancel edit" variant="ghost" onPress={() => { setForm(toForm(detail)); setEditing(false); setErr(null); }} />
            <AdminButton theme={THEME} label="Save changes" loading={saving} onPress={() => void onSave()} />
          </>
        ) : (
          <>
            <AdminButton theme={THEME} label="Open full editor →" variant="ghost" onPress={() => { onClose(); router.push(`/client/competition/${detail.id}/edit` as Href); }} />
            <AdminButton theme={THEME} label="Edit details" variant="outline" onPress={() => setEditing(true)} />
            <AdminButton theme={THEME} label="Close" onPress={onClose} />
          </>
        )
      }
    >
      <View style={{ gap: SPACING.lg }}>
        {err ? <AdminBanner theme={THEME} tone="error" message={err} /> : null}
        {savedNote ? <AdminBanner theme={THEME} tone="success" message={savedNote} /> : null}

        {/* Overview */}
        <View>
          <SectionHeader theme={THEME} title="Overview" tone="dark" />
          {editing ? (
            <View style={{ gap: 10 }}>
              <AdminInput theme={THEME} label="Case name" value={form.name} onChangeText={(v) => setForm({ ...form, name: v })} />
              <View>
                <Text style={labelStyle}>Tier</Text>
                <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 6 }}>
                  {ALL_TIERS.map((t) => (
                    <FilterChip key={t} theme={THEME} label={t === "unlimited" ? "Unlimited" : `${t} students`} active={form.tier === t} onPress={() => setForm({ ...form, tier: t })} />
                  ))}
                </View>
              </View>
            </View>
          ) : (
            <>
              <DetailRow k="Tier" v={detail.tier ?? "Not set"} />
              <DetailRow k="Engagement" v={engagementLabel(detail.engagement_model as never)} />
              <DetailRow k="Status" v={detail.status} />
              <DetailRow k="Company" v={detail.company_name ?? "—"} />
              <DetailRow k="Client" v={`${detail.client_full_name ?? "—"}${detail.client_email ? ` · ${detail.client_email}` : ""}`} />
            </>
          )}
        </View>

        {/* Scholarship + split */}
        <View>
          <SectionHeader theme={THEME} title="Scholarship & prize split" tone="dark" />
          <View style={cardStyle}>
            <Text style={{ fontFamily: THEME.displayMediumFont, fontSize: 22, color: THEME.accent }}>
              {formatCents(scholarshipTotalCents)}
            </Text>
            <Text style={{ marginTop: 2, fontFamily: THEME.bodyFont, fontSize: 11.5, color: THEME.textOnSurfaceDim }}>
              Total scholarship pool across {prizeRows.length || 0} place{prizeRows.length === 1 ? "" : "s"}
            </Text>
            <View style={{ marginTop: 10, gap: 4 }}>
              {prizeRows.length === 0 ? (
                <Text style={{ fontFamily: THEME.bodyFont, fontSize: 12, color: THEME.textOnSurfaceFaint }}>No prize structure set.</Text>
              ) : (
                prizeRows
                  .slice()
                  .sort((a, b) => (a.place ?? 99) - (b.place ?? 99))
                  .map((p, i) => (
                    <View key={i} style={{ flexDirection: "row", justifyContent: "space-between" }}>
                      <Text style={{ fontFamily: THEME.bodyMediumFont, fontSize: 12, color: THEME.textOnSurface }}>
                        {p.label || `Place ${p.place ?? i + 1}`}
                      </Text>
                      <Text style={{ fontFamily: THEME.bodyMediumFont, fontSize: 12, color: THEME.accent }}>
                        {formatCents(p.amount_cents ?? 0)}
                      </Text>
                    </View>
                  ))
              )}
            </View>
            {editing ? (
              <Text style={{ marginTop: 10, fontFamily: THEME.bodyFont, fontSize: 11, color: THEME.textOnSurfaceFaint }}>
                Per-place splits are edited in the full editor to keep prize math consistent.
              </Text>
            ) : null}
          </View>
        </View>

        {/* Schedule + team */}
        <View>
          <SectionHeader theme={THEME} title="Schedule & team" tone="dark" />
          {editing ? (
            <View style={{ gap: 10 }}>
              <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 10 }}>
                <View style={{ flexBasis: 220, flexGrow: 1 }}>
                  <AdminInput theme={THEME} label="Opens at" value={form.opens_at} onChangeText={(v) => setForm({ ...form, opens_at: v })} placeholder="YYYY-MM-DDTHH:MM" autoCapitalize="none" />
                </View>
                <View style={{ flexBasis: 220, flexGrow: 1 }}>
                  <AdminInput theme={THEME} label="Closes at" value={form.closes_at} onChangeText={(v) => setForm({ ...form, closes_at: v })} placeholder="YYYY-MM-DDTHH:MM" autoCapitalize="none" />
                </View>
              </View>
              <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 10 }}>
                <View style={{ flexBasis: 160, flexGrow: 1 }}>
                  <AdminInput theme={THEME} label="Min team size" value={form.team_size_min} onChangeText={(v) => setForm({ ...form, team_size_min: v.replace(/[^0-9]/g, "") })} keyboardType="numeric" />
                </View>
                <View style={{ flexBasis: 160, flexGrow: 1 }}>
                  <AdminInput theme={THEME} label="Max team size" value={form.max_team_size} onChangeText={(v) => setForm({ ...form, max_team_size: v.replace(/[^0-9]/g, "") })} keyboardType="numeric" />
                </View>
              </View>
            </View>
          ) : (
            <>
              <DetailRow k="Opens" v={formatFriendlyDate(detail.opens_at)} />
              <DetailRow k="Closes" v={formatFriendlyDate(detail.closes_at)} />
              <DetailRow k="Team size" v={`${detail.team_size_min ?? 1}–${detail.max_team_size ?? 4} members`} />
            </>
          )}
        </View>

        {/* Brief + deliverables */}
        <View>
          <SectionHeader theme={THEME} title="Brief & deliverables" tone="dark" />
          {editing ? (
            <View style={{ gap: 10 }}>
              <AdminInput theme={THEME} label="Brief" value={form.brief} onChangeText={(v) => setForm({ ...form, brief: v })} multiline minHeight={90} />
              <AdminInput theme={THEME} label="Deliverable description" value={form.deliverable_description} onChangeText={(v) => setForm({ ...form, deliverable_description: v })} multiline minHeight={90} />
              <Text style={{ fontFamily: THEME.bodyFont, fontSize: 11, color: THEME.textOnSurfaceFaint }}>
                Individual deliverable rows, due dates, rubric weights, and eligibility are edited in the full editor.
              </Text>
            </View>
          ) : (
            <View style={cardStyle}>
              <Text style={{ fontFamily: THEME.bodyFont, fontSize: 12.5, lineHeight: 18, color: THEME.textOnSurface }}>
                {detail.brief || "No brief provided."}
              </Text>
              {detail.deliverable_description ? (
                <Text style={{ marginTop: 10, fontFamily: THEME.bodyFont, fontSize: 12.5, lineHeight: 18, color: THEME.textOnSurfaceDim }}>
                  {detail.deliverable_description}
                </Text>
              ) : null}
            </View>
          )}
        </View>
      </View>
    </AdminModal>
  );
}

const labelStyle = {
  fontFamily: THEME.bodySemiBoldFont,
  fontSize: 10,
  letterSpacing: 2.2,
  textTransform: "uppercase" as const,
  color: THEME.textOnSurfaceFaint,
  marginBottom: 6,
};

const cardStyle = {
  padding: 14,
  borderRadius: RADII.md,
  borderWidth: STROKES.hairline,
  borderColor: THEME.border,
  backgroundColor: THEME.surfaceRaisedBg,
} as const;
